// ── Shared calculated-field utilities ─────────────────────────────────────────
// Used by both the form builder's CalculatedFieldsPanel and the live public-form
// viewer so formula evaluation and formatting always behave identically.

/**
 * Evaluate a formula string against a map of field values and an array of
 * product definitions.
 *
 * Supports:
 *   - {token}  – curly-brace references to field names OR product names
 *   - bareWord – single-word product/field name references (multi-word names
 *                must use the curly-brace syntax)
 *
 * Products take priority over form fields when a name collides.
 *
 * @param {string}   formula      – the formula string
 * @param {object}   fieldValues  – map of field name/id → current value
 * @param {Array}    products     – array of { name, price } objects
 * @returns {number|string} – numeric result, '', 'Error', or 'Invalid formula'
 */
export const evaluateFormula = (formula, fieldValues, products = []) => {
  if (formula == null || formula === '') return '';
  if (typeof formula !== 'string') return 'Invalid formula';
  if (!formula.trim()) return '';
  try {
    // Build augmented value map: field values + product name → price
    const augmented = { ...fieldValues };
    for (const p of (products || [])) {
      if (p?.name) augmented[p.name] = parseFloat(p?.price) || 0;
    }

    // Replace {token} references (handles both field names and product names)
    let expr = formula?.replace(/\{([^}]+)\}/g, (_, name) => {
      const val = parseFloat(augmented?.[name]);
      return isNaN(val) ? '0' : String(val);
    });

    // Replace bare identifier references — products first (higher priority), then fields.
    // Multi-word names can only be referenced with curly-brace syntax.
    const namedVars = [
      ...(products || [])
        ?.filter(p => p?.name && !/\s/.test(p?.name))
        ?.map(p => ({ name: p?.name, value: parseFloat(p?.price) || 0 })),
      ...Object.entries(fieldValues || {})
        ?.filter(([name]) => name && !/\s/.test(name))
        ?.map(([name, val]) => ({ name, value: parseFloat(val) || 0 })),
    ];
    const seen = new Set();
    for (const { name, value } of namedVars) {
      if (seen?.has(name)) continue;
      seen?.add(name);
      const safeName = name?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expr = expr?.replace(new RegExp(`\\b${safeName}\\b`, 'g'), String(value));
    }

    // Safety check: only allow math characters
    if (!/^[\d\s+\-*/().%,]+$/.test(expr)) return 'Invalid formula';
    // eslint-disable-next-line no-new-func
    const result = Function('"use strict"; return (' + expr + ')')();
    return isNaN(result) || !isFinite(result) ? 'Error' : result;
  } catch {
    return 'Error';
  }
};

/**
 * Format a raw numeric result according to the calc field's display settings.
 *
 * @param {number|string} value    – raw result from evaluateFormula
 * @param {string} format          – 'number' | 'currency' | 'percentage' | 'text'
 * @param {string} currency        – 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD'
 * @param {string|number} decimals – number of decimal places
 * @param {string} prefix          – optional prefix string
 * @param {string} suffix          – optional suffix string
 * @returns {string}
 */
export const formatResult = (value, format, currency, decimals, prefix, suffix) => {
  if (value === '' || value === 'Error' || value === 'Invalid formula') return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const parsedDecimals = Number.parseInt(decimals, 10);
  const dec = Number.isFinite(parsedDecimals)
    ? Math.max(0, Math.min(20, parsedDecimals))
    : 0;
  let formatted;
  if (format === 'currency') {
    const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$' };
    formatted = `${symbols?.[currency] || '$'}${num?.toFixed(dec)}`;
  } else if (format === 'percentage') {
    formatted = `${num?.toFixed(dec)}%`;
  } else {
    formatted = num?.toFixed(dec);
  }
  return `${prefix || ''}${formatted}${suffix || ''}`;
};
